import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field, Select } from '@/components/ui/field';
import { AREA_TYPES, apiMessage, setupApi } from '../spacesLib';
import type { Area, Floor, VenueTable } from '../spacesLib';

export type SpaceKind = 'floor' | 'area' | 'table';

interface Props {
  kind: SpaceKind;
  open: boolean;
  /** The row being edited, or null to create. */
  record: Floor | Area | VenueTable | null;
  /** Parent id — branchId for a floor, floorId for an area, areaId for a table. */
  parentId: string;
  onClose: () => void;
  onSaved: () => void;
}

type FormState = Record<string, string | boolean>;

const BLANK: Record<SpaceKind, FormState> = {
  floor: { name: '', level: '0', width: '1200', height: '800', isActive: true },
  area: { name: '', type: 'INDOOR', capacity: '', reservable: true, isActive: true },
  table: { number: '', name: '', seats: '4', isActive: true },
};

/** Create/edit for any rung of the hierarchy. One dialog, three shapes. */
export function SpaceDialog({ kind, open, record, parentId, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>(BLANK[kind]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (!record) {
      setForm(BLANK[kind]);
      return;
    }
    if (kind === 'floor') {
      const f = record as Floor;
      setForm({
        name: f.name,
        level: String(f.level),
        width: String(f.width),
        height: String(f.height),
        isActive: f.isActive,
      });
    } else if (kind === 'area') {
      const a = record as Area;
      setForm({
        name: a.name,
        type: a.type,
        capacity: a.capacity == null ? '' : String(a.capacity),
        reservable: a.reservable,
        isActive: a.isActive,
      });
    } else {
      const tb = record as VenueTable;
      setForm({
        number: tb.number,
        name: tb.name ?? '',
        seats: String(tb.seats),
        isActive: tb.isActive,
      });
    }
  }, [open, record, kind]);

  const set = (key: string, value: string | boolean) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const num = (v: unknown) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };

  const buildBody = (): Record<string, unknown> => {
    if (kind === 'floor') {
      return {
        ...(record ? {} : { branchId: parentId }),
        name: String(form.name).trim(),
        level: num(form.level) ?? 0,
        width: num(form.width) ?? 1200,
        height: num(form.height) ?? 800,
        isActive: form.isActive,
      };
    }
    if (kind === 'area') {
      return {
        ...(record ? {} : { floorId: parentId }),
        name: String(form.name).trim(),
        type: form.type,
        // Omitted rather than sent as null — the DTO validates it as a number.
        ...(String(form.capacity).trim() ? { capacity: num(form.capacity) } : {}),
        reservable: form.reservable,
        isActive: form.isActive,
      };
    }
    return {
      ...(record ? {} : { areaId: parentId }),
      number: String(form.number).trim(),
      name: String(form.name).trim(),
      seats: num(form.seats) ?? 4,
      isActive: form.isActive,
    };
  };

  /**
   * The `@IsNotEmpty()` fields on `Create{Floor,Area,Table}Dto`. A floor and an
   * area are named; a table is identified by its number and its name is
   * optional. Blank means a 400, so the reason sits on the button.
   */
  const missingRequired =
    kind === 'table' ? !String(form.number).trim() : !String(form.name).trim();
  const blockReason = missingRequired
    ? kind === 'table'
      ? t('setup.table.numberRequired')
      : t('setup.nameRequired')
    : null;

  const save = async () => {
    if (blockReason) return;
    setSaving(true);
    setError(null);
    try {
      const body = buildBody();
      const client = setupApi[kind === 'floor' ? 'floors' : kind === 'area' ? 'areas' : 'tables'];
      if (record) await client.update(record.id, body);
      else await client.create(body);
      toast.success(t(record ? 'setup.saved' : 'setup.created'));
      onSaved();
      onClose();
    } catch (e) {
      setError(apiMessage(e, t('setup.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const title = t(`setup.${kind}.${record ? 'edit' : 'create'}`);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => void save()}
            disabled={saving || blockReason !== null}
            title={blockReason ?? undefined}
          >
            {saving ? t('common.loading') : t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        {blockReason && (
          <p className="text-sm text-ink-muted">{blockReason}</p>
        )}

        {kind === 'table' && (
          <Field label={t('setup.table.number')} required hint={t('setup.table.numberHint')}>
            <Input value={String(form.number)} onChange={(e) => set('number', e.target.value)} />
          </Field>
        )}

        <Field label={t('setup.name')} required={kind !== 'table'}>
          <Input value={String(form.name)} onChange={(e) => set('name', e.target.value)} />
        </Field>

        {kind === 'floor' && (
          <>
            <Field label={t('setup.floor.level')} hint={t('setup.floor.levelHint')}>
              <Input
                inputMode="numeric"
                value={String(form.level)}
                onChange={(e) => set('level', e.target.value)}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t('setup.floor.width')} hint={t('setup.floor.canvasHint')}>
                <Input
                  inputMode="numeric"
                  value={String(form.width)}
                  onChange={(e) => set('width', e.target.value)}
                />
              </Field>
              <Field label={t('setup.floor.height')}>
                <Input
                  inputMode="numeric"
                  value={String(form.height)}
                  onChange={(e) => set('height', e.target.value)}
                />
              </Field>
            </div>
          </>
        )}

        {kind === 'area' && (
          <>
            <Field label={t('setup.area.type')}>
              <Select value={String(form.type)} onChange={(e) => set('type', e.target.value)}>
                {AREA_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {t(`setup.area.types.${type}`, type)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('setup.area.capacity')} hint={t('setup.area.capacityHint')}>
              <Input
                inputMode="numeric"
                value={String(form.capacity)}
                onChange={(e) => set('capacity', e.target.value)}
              />
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={Boolean(form.reservable)}
                onChange={(e) => set('reservable', e.target.checked)}
              />
              {t('setup.area.reservable')}
            </label>
          </>
        )}

        {kind === 'table' && (
          <Field label={t('setup.table.seats')}>
            <Input
              inputMode="numeric"
              value={String(form.seats)}
              onChange={(e) => set('seats', e.target.value)}
            />
          </Field>
        )}

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={Boolean(form.isActive)}
            onChange={(e) => set('isActive', e.target.checked)}
          />
          {t('setup.active')}
        </label>
      </div>
    </Dialog>
  );
}
